import React, { useEffect, useMemo, useState } from 'react'
import { PiCurrencyDollar, PiTrophy, PiTrendUp } from 'react-icons/pi'
import { TargetArrowIcon } from '../Layout/LeftNav/nav-icons'
import { useQuery } from '@tanstack/react-query'
import { cn } from '../UI/cn'
import { TabPanel } from '../FbUI/PillTabs'
import { UnderlineTabs } from '../FbUI/UnderlineTabs'
import { MetricCard } from '../FbUI/MetricCard'
import {
  getHomepageStats,
  getPerformanceStats,
  getPipelineValueStats,
  type HomepageStats,
  type PerformanceStats,
  type PerformanceRange,
  type PipelineValueStats,
} from '../../services/recommendations_api'
import {
  formatInteger,
  formatPercentage,
  formatCurrency,
} from '../../utils/formatting'
import useApiService from '../../services/api'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import {
  DEFAULT_STAGE_COLOR,
  getPipelineColorForStage,
} from '../SalesStage/stageColors'
import { ProgressChart } from '../NewCompanyPage/Sidebar/shared/ProgressChart'
import { PerformanceChart } from './PerformanceChart'
import type { PeriodType } from './PeriodToggle'
import { RangeSelector } from './RangeSelector'

const PERF_TAB_ID = 'performance-dashboard'

/** Shared card container styles used across homepage dashboard cards. */
const CARD_CONTAINER_STYLES =
  'bg-surface rounded-lg border border-line shadow-sm p-4'

interface PerformanceDashboardCardProps {
  /** Optional user ID for superusers to view another user's performance. */
  userId?: number
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Skeleton loader for the performance dashboard.
 *
 * Mirrors the exact layout of the loaded card to prevent content shift:
 * header → 4 metric cards → chart tabs + bar chart area.
 */
const PerformanceDashboardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn(CARD_CONTAINER_STYLES, className)}>
    <div className="animate-pulse">
      {/* Header: range label + range selector dropdown / "Monthly" badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-20 bg-surface-disabled rounded" />
          {/* RangeSelector (ReactSelect) approximation */}
          <div className="h-[38px] w-28 bg-surface-disabled rounded" />
        </div>
        <div className="h-3 w-12 bg-surface-disabled rounded" />
      </div>

      {/* 4 MetricCards: icon + bold number + label */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-surface rounded-lg border border-line p-3 text-center"
          >
            {/* Icon row */}
            <div className="flex items-center justify-center mb-1">
              <div className="h-4 w-4 bg-surface-disabled rounded" />
            </div>
            {/* Value */}
            <div className="h-7 w-12 bg-surface-disabled rounded mx-auto mb-1" />
            {/* Label */}
            <div className="h-3 w-16 bg-surface-raised rounded mx-auto" />
          </div>
        ))}
      </div>

      {/* Chart section: "MONTHLY TRENDS" label + tabs + chart area */}
      <div>
        <div className="h-3 w-28 bg-surface-disabled rounded mb-2" />
        {/* Tab row: Deals | Emails | Value */}
        <div className="flex gap-1 mb-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-7 w-14 bg-surface-disabled rounded" />
          ))}
        </div>
        {/* Bar chart area — matches h-56 */}
        <div className="h-56 bg-surface-disabled rounded" />
      </div>
    </div>
  </div>
)

/**
 * Performance dashboard showing historical metrics over time.
 *
 * Layout:
 * 1. Header with range selector and period toggle (Monthly/Weekly)
 * 2. Summary metrics for selected range (Deals Opened, Won, Win Rate, Value Opened, Value Won, Emails Sent)
 * 3. Bar chart with tabs (Deals, Emails, Value) showing trends over the selected time window
 */
export const PerformanceDashboardCard: React.FC<
  PerformanceDashboardCardProps
> = ({ userId, className }) => {
  const [stats, setStats] = useState<PerformanceStats | null>(null)
  const [pipelineValueStats, setPipelineValueStats] =
    useState<PipelineValueStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [periodType] = useState<PeriodType>('monthly')
  const [range, setRange] = useState<PerformanceRange>('6m')
  // Three tabs swap between the headline summary, the pipeline-by-
  // stage breakdown, and the trends chart. Summary is the default —
  // it's the at-a-glance view most users land on the home page for.
  type SectionTab = 'summary' | 'pipeline' | 'trends'
  const [activeSection, setActiveSection] = useState<SectionTab>('summary')

  // Pipeline-by-stage breakdown — sourced from the homepage-stats endpoint.
  // Same query key as the (now-removed) PipelineSnapshotCard so any other
  // caller of getHomepageStats reuses the cache entry.
  const { data: pipelineStats } = useQuery<HomepageStats | null>({
    queryKey: ['homepage-stats', userId ?? 'self'],
    queryFn: () => getHomepageStats(userId),
  })

  // Sales-stage list + custom-order preference drive the per-stage fill
  // color, so the pipeline chart matches the indigo/Closed-Won/Closed-Lost
  // palette used on stage pills and the deal detail page.
  const { data: allStages } = useApiService().useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const pipelineChartItems = useMemo(() => {
    if (!pipelineStats) return []
    const stagesWithDeals = pipelineStats.deals_by_stage.filter(
      (s) => s.count > 0
    )
    const totalDeals = stagesWithDeals.reduce((sum, s) => sum + s.count, 0)
    return stagesWithDeals.map((stage) => ({
      name: `${stage.stage_name} · ${formatCurrency(stage.total_value)} · ${stage.count} deal${stage.count !== 1 ? 's' : ''}`,
      value: stage.count,
      color:
        (allStages &&
          getPipelineColorForStage(
            stage.stage_id,
            allStages,
            customOrderEnabled
          )?.bg) ??
        DEFAULT_STAGE_COLOR,
      percentage:
        totalDeals > 0 ? Math.round((stage.count / totalDeals) * 100) : 0,
    }))
  }, [pipelineStats, allStages, customOrderEnabled])
  useEffect(() => {
    let isCancelled = false

    const fetchStats = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Pipeline value stats are fetched independently so a failure
        // doesn't break the rest of the dashboard — the Pipeline tab
        // simply won't appear.
        const perfData = await getPerformanceStats({
          userId,
          periodType,
          range,
        })
        if (!isCancelled) {
          setStats(perfData)
        }
      } catch (err) {
        if (!isCancelled) {
          const message =
            err instanceof Error
              ? err.message
              : 'Failed to load performance data'
          setError(message)
        }
      }

      try {
        const pipelineValueData = await getPipelineValueStats({
          userId,
          periodType,
          range,
        })
        if (!isCancelled) {
          setPipelineValueStats(pipelineValueData)
        }
      } catch {
        // Non-critical — Pipeline tab won't appear
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchStats()

    return () => {
      isCancelled = true
    }
  }, [userId, periodType, range])

  if (isLoading) {
    return <PerformanceDashboardSkeleton className={className} />
  }

  if (error) {
    return (
      <div className={cn(CARD_CONTAINER_STYLES, 'p-6', className)}>
        <div className="text-center text-fg-secondary">
          <p>Unable to load performance data</p>
          <p className="text-sm text-fg-muted mt-1">{error}</p>
        </div>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  const { totals, range_info, periods } = stats

  const performanceMetrics = [
    {
      key: 'deals-opened',
      label: 'Deals Opened',
      icon: <PiTrendUp size={16} />,
      value: formatInteger(totals.deals_opened),
      tooltipContent: `Number of deals opened in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'deals-won',
      label: 'Deals Won',
      icon: <PiTrophy size={16} />,
      value: formatInteger(totals.deals_won),
      tooltipContent: `Deals closed won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'value-won',
      label: 'Value Won',
      icon: <PiCurrencyDollar size={16} />,
      value: formatCurrency(totals.value_won),
      tooltipContent: `Annual value of deals won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'win-rate',
      label: 'Win Rate',
      icon: <TargetArrowIcon size={16} />,
      value:
        totals.overall_win_rate !== null
          ? `${formatPercentage(totals.overall_win_rate)}%`
          : '-',
      tooltipContent: `Win rate for ${range_info.range_label.toLowerCase()} (Won / (Won + Lost))`,
    },
  ]

  return (
    <div className={cn(CARD_CONTAINER_STYLES, className)}>
      {/* Header row — underline tabs on the left for switching between
          distinct content views (summary / pipeline / trends), range
          selector on the right. Pipeline tab is suppressed when empty. */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <UnderlineTabs<SectionTab>
          aria-label="Performance dashboard sections"
          idPrefix={PERF_TAB_ID}
          activeKey={activeSection}
          onChange={setActiveSection}
          tabs={[
            { key: 'summary', title: 'Your Performance' },
            ...(pipelineChartItems.length > 0
              ? ([{ key: 'pipeline', title: 'Pipeline by stage' }] as const)
              : []),
            {
              key: 'trends',
              title: `${periodType === 'monthly' ? 'Monthly' : 'Weekly'} Trends`,
            },
          ]}
        />
        <div className="flex-shrink-0">
          <RangeSelector value={range} onChange={setRange} />
        </div>
      </div>

      <TabPanel<SectionTab>
        idPrefix={PERF_TAB_ID}
        tabKey="summary"
        activeKey={activeSection}
        className="grid grid-cols-4 gap-3"
      >
        {performanceMetrics.map((metric) => (
          <MetricCard
            key={metric.key}
            icon={metric.icon}
            value={metric.value}
            label={metric.label}
            tooltipContent={metric.tooltipContent}
          />
        ))}
      </TabPanel>

      {pipelineChartItems.length > 0 && (
        <TabPanel<SectionTab>
          idPrefix={PERF_TAB_ID}
          tabKey="pipeline"
          activeKey={activeSection}
        >
          <ProgressChart
            items={pipelineChartItems}
            tooltipValueFormatter={(item) =>
              `${item.value} deal${item.value !== 1 ? 's' : ''}`
            }
            legend={{
              title: 'Sales Stages',
              columns: 2,
              showPercentages: true,
            }}
            bareLegend
          />
        </TabPanel>
      )}

      <TabPanel<SectionTab>
        idPrefix={PERF_TAB_ID}
        tabKey="trends"
        activeKey={activeSection}
      >
        <PerformanceChart
          periods={periods}
          pipelinePeriods={pipelineValueStats?.periods}
        />
      </TabPanel>
    </div>
  )
}
